import swal from "sweetalert";
import moment from "moment";
import { usePost } from "seed/api";
import { useSet } from "seed/gql";
import { SET_COLLECTION } from "seed/gql/queries";
import { downloadFile } from "components/util/file_util";
import l from "components/util/l";

import lazyCall from "components/util/helpers/LazyCall";
const exportExcelFile = lazyCall("exportExcelFile", "components/collections/util/excel");

const CollectionCallbacks = ({
	tableRef,
	flowId,
	activeCollectionId,
	setActiveCollectionId,
	auxCollections,
	setSearch,
	setQuery,
	setFilters,
	setSorting,
	setPageNum,
	setLastFilterDate,
	setEmbeddingStatus,
	refetchCollections,
	refetchParentCollections,
	refetchActiveCollection,
	refetchData,
}) => {
	const [callSetCollection] = useSet(SET_COLLECTION, {
		onCompleted: (data) => {
			refetchCollections();
			refetchParentCollections();
		}
	});

	const [callCreateCollection] = usePost("/collections/create_collection", {
		onCompleted: (data) => {
			refetchCollections(true);
			refetchParentCollections();
		}
	});

	const [callCloneCollection] = usePost("/collections/clone_collection", {
		onCompleted: (data) => {
			refetchCollections();
		}
	});

	const [callDeleteCollection] = usePost("/collections/delete_collection", {
		onCompleted: (data) => {
			refetchCollections();
			refetchParentCollections();
			setActiveCollectionId(null);
		}
	});

	const onChangeCollectionName = (collection, name) => {
		callSetCollection({
			id: collection.id,
			uiSettings: {
				...collection.uiSettings,
				label: name,
			}
		});
	}

	const onClickCreateCollection = () => {
		const columnName = crypto.randomUUID().replace(/-/g, '');
		callCreateCollection({
			name: `${l("Tabla%GA054")} ${auxCollections.length + 1}`,
			primary_key: columnName,
			type: "TABLE",
			flow_id: flowId,
			structure: [{
				final: {
					"name": columnName,
					"label": `${l("Campo%CA014")} 1`,
					"type": "string",
				},
				action: "create"
			}]
		});
		tableRef.current?.cleanSelection();
	}

	const onSaveCollection = (data) => {
		setActiveCollectionId(data.id);
		refetchCollections();
		refetchParentCollections();
	}

	const onEditCollection = () => {
		refetchCollections();
		refetchParentCollections();
		refetchActiveCollection();
		refetchData();
	}

	const onClickCloneCollection = (values) =>
		callCloneCollection({
			collection_id: activeCollectionId,
			...values
		});

	const onClickChangeTab = (collectionId) => {
		localStorage.setItem("def_collection_" + flowId, collectionId)
		setSearch("");
		setQuery(localStorage.getItem("collection_query_" + collectionId) || "");
		setFilters(JSON.parse(localStorage.getItem("collection_filters_" + collectionId) || "[]"));
		setSorting(JSON.parse(localStorage.getItem("collection_sorting_" + collectionId) || "[]"));
		setPageNum(parseInt(localStorage.getItem("collection_page_" + collectionId) || "1"));
		setLastFilterDate(new Date().toISOString());
		setActiveCollectionId(collectionId);
		setEmbeddingStatus(null);
		tableRef.current?.cleanSelection();
	}

	const onClickDeleteCollection = (collectionId) =>
		swal({
			title: l("Eliminar tabla%CA138"),
			icon: "warning",
			text: l("¿Estás seguro que desea eliminar esta tabla?%CA139"),
			buttons: {
				confirm: {
					text: l("Aceptar%GA020"),
					className: "swal-button swal-button--cancel btn-success",
				},
				cancel: l("Cancelar%GA021")
			},
		}).then((response) => {
			if (response)
				callDeleteCollection({ collection_id: collectionId });
		});

	return {
		onSaveCollection,
		onEditCollection,
		onChangeCollectionName,
		onClickCreateCollection,
		onClickCloneCollection,
		onClickChangeTab,
		onClickDeleteCollection,
	};

}

const RowCallbacks = ({
	tableRef,
	activeCollectionId,
	rows,
	pushOperationQueue,
	setIsLoading,
}) => {

	const renderDependancies = (dependancies, level = 1) => {
		
		let output = "";

		for (const dep of dependancies) {
			
			const indent = "    ".repeat(level);
			output += `\n${indent}• ${dep.collection_name} - ${dep.data.pk_label || dep.data.id}`;
			
			if (dep.dependancies && dep.dependancies.length > 0)
				output += renderDependancies(dep.dependancies, level + 1);

		}

		return output;

	}


	const checkValidationResult = (data) => {

		const actions = [];
		let dependancies = "";
		let valid = true;

		for (const validationData of data) {

			valid = valid && validationData.valid;

			actions.push({
				initial: validationData.collection_data,
				action: "delete",
				cascade: true,
			});

			if (!validationData.valid) {
				dependancies += `\n\n${validationData.collection_name} - ${validationData.collection_data.pk_label}`;
				dependancies += renderDependancies(validationData.dependancies, 1);
			}

		}

		const args = {
			actions,
			type: "data",
			collection_id: activeCollectionId
		};

		if (valid) {
			pushOperationQueue(args);
			tableRef.current?.cleanSelection();
			return;
		}

		swal({
			title: l("Eliminar registro%CA044"),
			icon: "warning",
			text: `${l("¿Estás seguro que desea eliminar esta registro? las siguientes referencias se verán afectadas%CA140")}: \n`
				+ dependancies,
			html: true,
			buttons: {
				delete_set_null: {
					text: l("Eliminar%GA001"),
					className: "swal-button swal-button--cancel btn-danger px-2 w-100",
				},
				delete_cascade: {
					text: l("Eliminar registro y vinculados%CA142"),
					className: "swal-button swal-button--cancel btn-danger px-2 w-100",
				},
				exit: {
					text: l("Cancelar%GA021"),
					className: "swal-button swal-button--cancel btn-secondary px-2 w-100",
				}
			},
		}).then((response) => {

			if (response == "delete_cascade") {
				args.actions = actions.map((a) => ({
					...a,
					cascade: true,
				}));
				pushOperationQueue(args);
				tableRef.current?.cleanSelection();
				return;
			}

			if (response == "delete_set_null") {
				args.actions = actions.map((a) => ({
					...a,
					cascade: false,
				}));
				pushOperationQueue(args);
				tableRef.current?.cleanSelection();
				return;
			}

			setIsLoading(false);

		});

	}

	const [callValidateDeleteRow] = usePost("/collections/check_data_dependancies", {
		onCompleted: (data) => {
			checkValidationResult(data);
		}
	});

	const onClickDeleteRow = (index) => {
		setIsLoading(true);
		callValidateDeleteRow({ collection_id: activeCollectionId, collection_data_ids: [rows[parseInt(index)].id] });
	}

	const onClickDeleteMultipleRows = (ids) => {
		setIsLoading(true);
		callValidateDeleteRow({ collection_id: activeCollectionId, collection_data_ids: ids });
	}

	const onClickClearData = () => {
		swal({
			title: l("Eliminar todos los datos%CA160"),
			icon: "warning",
			text: l("¿Estás seguro que desea eliminar todos los datos de esta tabla?%CA161"),
			buttons: {
				confirm: {
					text: l("Aceptar%GA020"),
					className: "swal-button swal-button--cancel btn-danger",
				},
				cancel: l("Cancelar%GA021")
			},
		}).then((response) => {
			if (response)
				pushOperationQueue({
					actions: [{ action: "clear" }],
					type: "data",
					collection_id: activeCollectionId
				});
		});
	}

	return {
		onClickDeleteRow,
		onClickDeleteMultipleRows,
		onClickClearData,
	};

}

const ViewCallbacks = ({
	activeCollectionId,
	collectionOptions,
	rows,
	columns,
	setRows,
	setIsLoading,
	refetchData,
	refetchActiveCollection,
}) => {

	const [callImportRows] = usePost("/collections/import_data", {
		onCompleted: () => {
			setIsLoading(false);
			refetchData();
			refetchActiveCollection();
		}
	});

	const [callGetExportData] = usePost("/collections/get_data", {
		onCompleted: (data) => {
			exportExcelFile({
				columns,
				collections: collectionOptions,
				rows: data.data,
				fileName: `${data.collection.ui_settings.label}_${moment().format("YYYYMMDDHHmmss")}.xlsx`,
				primaryKey: data.collection.schema.primary_key,
			});
		}
	});

	const [callDownloadZip] = usePost("/collections/download_files", {
		onCompleted: (data) => {
			downloadFile(data.url, null, data.filename);
		}
	});

	const onClickImport = (data) => {
		setIsLoading(true);
		callImportRows({
			collection_id: activeCollectionId,
			...data
		});
	}

	const onClickExport = (limit) => {
		callGetExportData({
			limit,
			collection_id: activeCollectionId,
			ignore_compress: true,
		});
	}

	const onClickDownloadZip = (field_name, file_limit) =>
		callDownloadZip({ collection_id: activeCollectionId, field_name, file_limit });

	const onClickCell = (data, i, j) => { }

	return {
		onClickImport,
		onClickExport,
		onClickDownloadZip,
		onClickCell
	}

}

export {
	CollectionCallbacks,
	RowCallbacks,
	ViewCallbacks
}